/**
 * useWebSocket Hook
 *
 * Thin subscription wrapper over the singleton WebSocketProvider.
 *
 * Consumes both the Actions context (stable — never re-renders on its own)
 * and the State context (changes only on WS open/close + initial unread
 * snapshot, both rare events). Public API unchanged from previous
 * iterations: returns { connected, unreadCount, sendMessage, reconnect,
 * fetchUnreadMessages }.
 *
 * For maximum perf, components that subscribe to events but don't display
 * connection status can call `useWebSocket(handler)` without destructuring
 * — they will re-render on rare WS open/close transitions but never on
 * frequent message events.
 */
import { useEffect, useRef } from 'react';
import {
  useWebSocketActions,
  useWebSocketState,
} from '../context/WebSocketContext';

export const useWebSocket = (onMessage) => {
  const { subscribe, sendMessage, reconnect, fetchUnreadMessages } =
    useWebSocketActions();
  const { connected, unreadCount } = useWebSocketState();

  const onMessageRef = useRef(onMessage);
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    if (typeof onMessageRef.current !== 'function') return undefined;
    const unsubscribe = subscribe((data) => {
      const handler = onMessageRef.current;
      if (typeof handler === 'function') handler(data);
    });
    return unsubscribe;
  }, [subscribe]);

  return { connected, unreadCount, sendMessage, reconnect, fetchUnreadMessages };
};

export default useWebSocket;
